import {
  Auth0AccountRole,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  SessionFrequency,
} from "client/openapi";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageStatus } from "types";
import { returnSubjectName } from "util/concatenateSubject";
import "./index.css";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../../../../src/sass/custom.scss";
import Meeting from "./MeetingBlocks/Meeting";
import RescheduleConfirmDialog from "./RescheduleConfirmDialog";
import GutterBookingProvider from "./GutterBooking/GutterBookingProvider";
import GutterDayColumn from "./GutterBooking/GutterDayColumn";

type CalendarViewType = "week" | "day";

const CalendarView = {
  WEEK: "week" as CalendarViewType,
  DAY: "day" as CalendarViewType,
};

export type CalendarEvent = {
  id: number;
  title: string;
  start: Date;
  end: Date;
  locked: boolean;
  isRecurring: boolean;
};

const DnDCalendar = withDragAndDrop(Calendar);

function mapToCalendarEvents(
  meetings: MeetingAdminResponse[] | MeetingLimitedResponse[]
): CalendarEvent[] {
  return meetings.map((m) => {
    const localStart = moment(m.start).format();
    const localEnd = moment(localStart).add(m.duration, "minutes").toDate();

    return {
      id: m.id,
      title: m.name ?? returnSubjectName(m.org_subject),
      start: moment(localStart).toDate(),
      end: localEnd,
      locked: m.locked,
      isRecurring: m.session.frequency !== SessionFrequency.SINGLE,
    };
  });
}

function CalendarWrapper({
  role,
  weekStart,
  events,
  setEvents,
  days,
  status,
  calendarViewType,
}: {
  role: Auth0AccountRole;
  events: MeetingAdminResponse[] | MeetingLimitedResponse[];
  setEvents: () => Promise<void>;
  weekStart: Date;
  days: number;
  status: PageStatus;
  calendarViewType: CalendarViewType;
}) {
  const localizer = momentLocalizer(moment);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [pendingReschedule, setPendingReschedule] = useState<{
    meeting: MeetingAdminResponse | MeetingLimitedResponse;
    newStart: Date;
    newDuration: number;
  } | null>(null);

  const canDrag = [
    Auth0AccountRole.ORG_ADMIN,
    Auth0AccountRole.ORG_TUTOR,
  ].includes(role);

  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const scrollCleanupRef = useRef<(() => void) | null>(null);

  // During drag: block library's preview-based scroll, use cursor-based scroll instead
  const setupScrollCap = useCallback(() => {
    const wrapper = calendarWrapperRef.current;
    if (!wrapper) return;
    const scrollContainer = wrapper.querySelector(
      ".rbc-time-content"
    ) as HTMLElement;
    if (!scrollContainer) return;

    const desc =
      Object.getOwnPropertyDescriptor(
        Object.getPrototypeOf(scrollContainer),
        "scrollTop"
      ) || Object.getOwnPropertyDescriptor(Element.prototype, "scrollTop");
    if (!desc) return;

    // Block library's scrollTop writes entirely during drag
    Object.defineProperty(scrollContainer, "scrollTop", {
      get() {
        return desc.get!.call(this);
      },
      set() {
        // no-op: all scrolling handled by cursor-based edge scroll below
      },
      configurable: true,
    });

    // Cursor-based edge scroll
    const EDGE_THRESHOLD = 80;
    const MAX_SCROLL_SPEED = 12;
    let edgeScrollRAF: number | null = null;
    let edgeScrollDelta = 0;

    const doEdgeScroll = () => {
      if (edgeScrollDelta === 0) {
        edgeScrollRAF = null;
        return;
      }
      const current = desc.get!.call(scrollContainer);
      desc.set!.call(scrollContainer, current + edgeScrollDelta);
      edgeScrollRAF = requestAnimationFrame(doEdgeScroll);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = scrollContainer.getBoundingClientRect();
      const distFromTop = e.clientY - rect.top;
      const distFromBottom = rect.bottom - e.clientY;

      if (distFromTop < EDGE_THRESHOLD) {
        const proximity = Math.max(0, distFromTop) / EDGE_THRESHOLD;
        edgeScrollDelta = -MAX_SCROLL_SPEED * (1 - proximity);
      } else if (distFromBottom < EDGE_THRESHOLD) {
        const proximity = Math.max(0, distFromBottom) / EDGE_THRESHOLD;
        edgeScrollDelta = MAX_SCROLL_SPEED * (1 - proximity);
      } else {
        edgeScrollDelta = 0;
      }

      if (edgeScrollDelta !== 0 && !edgeScrollRAF) {
        edgeScrollRAF = requestAnimationFrame(doEdgeScroll);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);

    scrollCleanupRef.current = () => {
      delete (scrollContainer as any).scrollTop;
      document.removeEventListener("mousemove", handleMouseMove);
      if (edgeScrollRAF) cancelAnimationFrame(edgeScrollRAF);
    };
  }, []);

  const teardownScrollCap = useCallback(() => {
    if (scrollCleanupRef.current) {
      scrollCleanupRef.current();
      scrollCleanupRef.current = null;
    }
  }, []);

  // Watch for drag start/end via the rbc-addons-dnd-is-dragging class
  useEffect(() => {
    const wrapper = calendarWrapperRef.current;
    if (!wrapper) return;
    const dndEl = wrapper.querySelector(".rbc-addons-dnd") as HTMLElement;
    if (!dndEl) return;

    const observer = new MutationObserver(() => {
      const isDragging = dndEl.classList.contains("rbc-addons-dnd-is-dragging");
      if (isDragging && !scrollCleanupRef.current) {
        setupScrollCap();
      } else if (!isDragging && scrollCleanupRef.current) {
        teardownScrollCap();
      }
    });

    observer.observe(dndEl, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
      teardownScrollCap();
    };
  }, [setupScrollCap, teardownScrollCap]);

  // Convert the raw events into RBC events:
  useEffect(() => {
    setCalendarEvents(mapToCalendarEvents(events));
  }, [events]);

  const handleEventDrop = ({ event, start }) => {
    const originalMeeting = events.find((m) => m.id === event.id);
    if (!originalMeeting) return;
    if (moment(start).isSame(moment(originalMeeting.start))) return;
    setPendingReschedule({
      meeting: originalMeeting,
      newStart: start as Date,
      newDuration: originalMeeting.duration,
    });
  };

  // Example custom event component for RBC:
  const CustomEvent = ({ event }) => {
    // If you have more data in "events", you can find the original meeting
    const originalMtg = events.find((m) => m.id === event.id);
    if (!originalMtg) return null;

    // Decide if you want to render your existing <Meeting> or <EventBlock>:
    return (
      <div className="meeting-container">
        <Meeting
          meeting={originalMtg}
          meetings={events}
          index={event.id}
          role={role}
          setEvents={setEvents}
          displayStart={event.start}
          displayEnd={event.end}
        />
      </div>
    );
  };

  return (
    <div
      ref={calendarWrapperRef}
      className={canDrag ? "tp-cal--bookable" : undefined}
    >
      {/* The provider keeps this calendar element as is, so a gutter
          selection never re-renders CalendarWrapper or its meetings. */}
      <GutterBookingProvider
        role={role}
        refetch={setEvents}
        dayView={calendarViewType === CalendarView.DAY}
        viewKey={`${calendarViewType}:${weekStart.getTime()}`}
      >
        <DnDCalendar
          // Let RBC handle the logic for stacking/overlapping
          dayLayoutAlgorithm="overlap"
          localizer={localizer}
          events={calendarEvents}
          view={calendarViewType === CalendarView.DAY ? Views.DAY : Views.WEEK}
          date={weekStart}
          defaultDate={weekStart}
          // For a custom header
          components={{
            week: {
              header: ({ date }) => (
                <div>
                  <span className="calendar--date-number">
                    {localizer.format(date, "DD")}
                  </span>
                  <span className="calendar--day-separator px-3">|</span>
                  {localizer.format(date, "dddd")}
                </div>
              ),
            },
            event: CustomEvent,
            // Admins and tutors get the booking strip on each day column.
            ...(canDrag ? { dayColumnWrapper: GutterDayColumn } : {}),
          }}
          formats={{
            timeGutterFormat: (date, culture) =>
              localizer.format(date, "h A", culture),
          }}
          scrollToTime={moment()
            .subtract(moment().hour() !== 0 ? 1 : 0, "hour")
            .toDate()}
          step={15}
          timeslots={4}
          toolbar={false}
          onNavigate={() => {}}
          onView={() => {}}
          popup
          draggableAccessor={(event: CalendarEvent) => canDrag && !event.locked}
          resizableAccessor={() => false}
          onEventDrop={handleEventDrop}
        />
      </GutterBookingProvider>
      {pendingReschedule && (
        <RescheduleConfirmDialog
          meeting={pendingReschedule.meeting}
          newStart={pendingReschedule.newStart}
          newDuration={pendingReschedule.newDuration}
          onConfirm={() => {
            setPendingReschedule(null);
            setEvents();
          }}
          onCancel={() => {
            setPendingReschedule(null);
            setCalendarEvents(mapToCalendarEvents(events));
          }}
        />
      )}
    </div>
  );
}

export default CalendarWrapper;
